// Client for graham's `/tcm` router.

import { apiCall } from "@/lib/api"
import type {
  TcmActionRequest,
  TcmBoard,
  TcmBridgeCreate,
  TcmBridgeUpdate,
  TcmCarePlan,
  TcmEpisodeDetail,
  TcmLang,
  TcmNoteBody,
  TcmResource,
  TcmResourceBody,
  TcmSummary,
} from "./types"

export const tcmApi = {
  getBoard: (orgId: string, scope: "active" | "closed") =>
    apiCall<TcmBoard>({ method: "GET", url: `/tcm/org/${orgId}/board`, params: { scope } }),

  getSummary: () => apiCall<TcmSummary>({ method: "GET", url: "/tcm/summary" }),

  getEpisode: (episodeId: string) =>
    apiCall<TcmEpisodeDetail>({ method: "GET", url: `/tcm/episode/${episodeId}` }),

  getNote: (episodeId: string) =>
    apiCall<TcmNoteBody>({ method: "GET", url: `/tcm/episode/${episodeId}/note` }),

  getNotePdf: (episodeId: string) =>
    apiCall<Blob>({ method: "GET", url: `/tcm/episode/${episodeId}/note.pdf`, responseType: "blob" }),

  act: (episodeId: string, body: TcmActionRequest) =>
    apiCall<TcmEpisodeDetail>({ method: "POST", url: `/tcm/episode/${episodeId}/action`, data: body }),

  getCarePlan: (episodeId: string) =>
    apiCall<TcmCarePlan>({ method: "GET", url: `/tcm/episode/${episodeId}/care-plan` }),

  /** The patient's 4-week plan as it prints now; `preview` highlights the blanks. */
  getHandoutPdf: (episodeId: string, lang: TcmLang, preview: boolean) =>
    apiCall<Blob>({
      method: "GET",
      url: `/tcm/episode/${episodeId}/handout.pdf`,
      params: { lang, preview },
      responseType: "blob",
    }),

  updateBridge: (episodeId: string, bridgeId: string, body: TcmBridgeUpdate) =>
    apiCall<TcmCarePlan>({ method: "PUT", url: `/tcm/episode/${episodeId}/bridge/${bridgeId}`, data: body }),

  addBridge: (episodeId: string, body: TcmBridgeCreate) =>
    apiCall<TcmCarePlan>({ method: "POST", url: `/tcm/episode/${episodeId}/bridge`, data: body }),

  getResources: (orgId: string) => apiCall<TcmResource[]>({ method: "GET", url: `/tcm/org/${orgId}/resources` }),

  addResource: (orgId: string, body: TcmResourceBody) =>
    apiCall<TcmResource>({ method: "POST", url: `/tcm/org/${orgId}/resources`, data: body }),

  updateResource: (orgId: string, resourceId: string, body: TcmResourceBody) =>
    apiCall<TcmResource>({ method: "PUT", url: `/tcm/org/${orgId}/resources/${resourceId}`, data: body }),
}

/** The API's refusal text ("Only 99495 can be billed for this visit."), or a fallback. */
export function tcmErrorMessage(err: any): string {
  const detail = err?.response?.data?.detail
  if (typeof detail === "string") return detail
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? String(d)).join("; ")
  return err?.message || "Something went wrong"
}
